export type UiLang = 'en' | 'lt';

export const UI_LANGUAGES: UiLang[] = ['en', 'lt'];

export const STATIC_ROUTE_SEGMENTS = {
    home: { en: '', lt: '' },
    services: { en: 'services', lt: 'paslaugos' },
    pricing: { en: 'pricing', lt: 'kainos' },
    languages: { en: 'languages', lt: 'kalbos' },
    contact: { en: 'contact', lt: 'kontaktai' },
    about: { en: 'about', lt: 'apie-mus' },
    faq: { en: 'faq', lt: 'duk' },
    privacy: { en: 'privacy', lt: 'privatumas' },
    terms: { en: 'terms', lt: 'salygos' },
    news: { en: 'news', lt: 'naujienos' },
    careers: { en: 'careers', lt: 'karjera' },
    order: { en: 'order', lt: 'uzsakymas' },
} as const;

export type StaticPageRouteKey = keyof typeof STATIC_ROUTE_SEGMENTS;

export const CAREER_RESOURCE_SEGMENTS = {
    about: { en: 'about-firm', lt: 'apie-mus' },
    values: { en: 'our-values', lt: 'vertybes' },
    culture: { en: 'our-culture', lt: 'musu-kultura' },
    inclusion: { en: 'culture-inclusion', lt: 'kultura-ir-itrauktis' },
    esg: { en: 'esg-impact', lt: 'tvarumas' },
    apply: { en: 'how-to-apply', lt: 'kandidatavimas' },
    tips: { en: 'interview-tips', lt: 'patarimai' },
    competencies: { en: 'competencies', lt: 'kompetencijos' },
    accessibility: { en: 'disability-support', lt: 'pagalba-neigaliesiems' },
} as const;

export const CONTENT_CATEGORY_SEGMENTS: Record<string, Record<UiLang, string>> = {
    news: STATIC_ROUTE_SEGMENTS.news,
    career: STATIC_ROUTE_SEGMENTS.careers,
    service: STATIC_ROUTE_SEGMENTS.services,
    tier: STATIC_ROUTE_SEGMENTS.pricing,
    delivery: { en: 'delivery', lt: 'delivery' },
    legal: { en: 'legal', lt: 'legal' },
    general: { en: 'pages', lt: 'pages' },
};

export const normalizeRoutePath = (value?: string) => {
    if (!value) return '/';
    const trimmed = value.trim();
    if (!trimmed) return '/';
    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (/^\/(?:en|lt)\/?$/.test(withLeadingSlash)) {
        return withLeadingSlash.endsWith('/') ? withLeadingSlash : `${withLeadingSlash}/`;
    }
    return withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')
        ? withLeadingSlash.slice(0, -1)
        : withLeadingSlash;
};

export const stripLocalePrefix = (value: string) => {
    const { lang, relativePath } = splitLocalizedPath(value);
    return { lang, relativePath };
};

export const splitLocalizedPath = (value?: string) => {
    const normalized = normalizeRoutePath(value);
    const segments = normalized.split('/').filter(Boolean);

    let localeBoundary = 0;
    while (localeBoundary < segments.length && UI_LANGUAGES.includes(segments[localeBoundary] as UiLang)) {
        localeBoundary += 1;
    }

    if (localeBoundary === 0) {
        return {
            lang: null,
            normalizedPath: normalized,
            relativePath: normalized,
            redundantLocaleCount: 0,
        };
    }

    const lang = segments[localeBoundary - 1] as UiLang;
    const relativeSegments = segments.slice(localeBoundary);
    const relativePath = relativeSegments.length > 0 ? `/${relativeSegments.join('/')}` : '/';
    const normalizedPath = relativeSegments.length > 0 ? `/${lang}/${relativeSegments.join('/')}` : `/${lang}/`;

    return {
        lang,
        normalizedPath,
        relativePath,
        redundantLocaleCount: localeBoundary - 1,
    };
};

export const getStaticPageSlug = (key: StaticPageRouteKey, lang: UiLang) => STATIC_ROUTE_SEGMENTS[key][lang];

export const getStaticPagePath = (key: StaticPageRouteKey, lang: UiLang) => {
    const slug = getStaticPageSlug(key, lang);
    return slug ? `/${lang}/${slug}` : `/${lang}/`;
};

export const getStaticPageLocalizedPaths = (key: StaticPageRouteKey) => ({
    en: getStaticPagePath(key, 'en'),
    lt: getStaticPagePath(key, 'lt'),
});

export const getContentCategoryBasePath = (category: string, lang: UiLang) => {
    const categoryKey = String(category || '').trim().toLowerCase();
    const match = CONTENT_CATEGORY_SEGMENTS[categoryKey];
    const slug = match?.[lang] || CONTENT_CATEGORY_SEGMENTS.general[lang];
    return `/${lang}/${slug}`;
};

export const getContentPagePath = (category: string, slug: string, lang: UiLang) => {
    const rawSlug = String(slug || '').trim();
    if (rawSlug === '#') return '#';
    const normalizedSlug = rawSlug.replace(/^\/+|\/+$/g, '');
    if (!normalizedSlug) return getContentCategoryBasePath(category, lang);
    return `${getContentCategoryBasePath(category, lang)}/${normalizedSlug}`;
};

export const getServiceDetailPath = (slug: string, lang: UiLang) => getContentPagePath('service', slug, lang);
export const getPricingDetailPath = (slug: string, lang: UiLang) => getContentPagePath('tier', slug, lang);
export const getNewsArticlePath = (slug: string, lang: UiLang) => getContentPagePath('news', slug, lang);
export const getCareerResourcePath = (slug: string, lang: UiLang) => getContentPagePath('career', slug, lang);
export const getJobDetailPath = (slug: string, lang: UiLang) => getCareerResourcePath(slug, lang);

const buildReverseSegmentMap = <T extends Record<string, Record<UiLang, string>>>(map: T) => ({
    en: Object.fromEntries(
        Object.values(map)
            .filter((segments) => segments.lt)
            .map((segments) => [segments.lt, segments.en]),
    ) as Record<string, string>,
    lt: Object.fromEntries(
        Object.values(map)
            .filter((segments) => segments.en)
            .map((segments) => [segments.en, segments.lt]),
    ) as Record<string, string>,
});

const STATIC_TRANSLATION_MAP = buildReverseSegmentMap(STATIC_ROUTE_SEGMENTS);
const CAREER_RESOURCE_TRANSLATION_MAP = buildReverseSegmentMap(CAREER_RESOURCE_SEGMENTS);

export const translateRelativeRoute = (relativePath: string, targetLang: UiLang) => {
    const segments = normalizeRoutePath(relativePath).split('/').filter(Boolean);
    if (segments.length === 0) return '';

    const translatedSegments = [...segments];
    translatedSegments[0] = STATIC_TRANSLATION_MAP[targetLang][segments[0]] || segments[0];

    const isCareerPath = translatedSegments[0] === STATIC_ROUTE_SEGMENTS.careers[targetLang]
        || segments[0] === STATIC_ROUTE_SEGMENTS.careers.en
        || segments[0] === STATIC_ROUTE_SEGMENTS.careers.lt;

    if (isCareerPath && segments.length > 1) {
        translatedSegments[1] = CAREER_RESOURCE_TRANSLATION_MAP[targetLang][segments[1]] || segments[1];
    }

    return `/${translatedSegments.join('/')}`;
};
